/**
 * zapd-engine-bridge.js — Adapter between the JS ZapPlayer wrapper and the
 * TS WASM+WebCodecs+WebGPU engine.
 *
 * Since the hls.js retirement (2026-05-08) the zapd-WASM-engine is the
 * SOLE player path — no fallback. Browsers without WebCodecs / WebAssembly
 * see an error overlay (`showAttachErrorOverlay`).
 *
 * Strategy (minimal-invasive):
 *   - Hide the native <video> element (display:none).
 *   - Create a sibling <canvas> as the render-target.
 *   - Mirror the engine's media-clock back into video.currentTime so the
 *     existing scrubber/time-display continue to work (they read
 *     video.currentTime + video.duration + video.buffered).
 *   - Forward play()/pause()/seek() through the engine.
 *
 * On detach: stop the engine, restore the <video>, remove the canvas.
 */

import { MainThreadBridge } from './main-thread/main-bridge.ts';
import { hlsBus } from '../core/hls_bus.js';
import { systemBus } from '../core/system_bus.js';

/**
 * Capability-detect for the WASM+WebCodecs engine. Throws a user-facing
 * Error if the browser can't run it — the caller (`_attachHlsSource` in
 * zap-player.js) lets that bubble up to the modal-error-banner via
 * `attachZapdEngine`'s catch-block.
 *
 * Since hls.js retirement there is no fallback player — old browsers
 * (Safari < 16.4, Firefox < 130 without `media.webcodecs.enabled`) get
 * a "Browser too old" error.
 */
function assertEngineSupported() {
  if (typeof WebAssembly === 'undefined') {
    throw new Error('WebAssembly not supported — browser too old.');
  }
  if (typeof window.VideoDecoder === 'undefined') {
    throw new Error(
      'WebCodecs API not supported — Chrome 94+ / Safari 16.4+ / Firefox 130+ required.',
    );
  }
  // The engine shares decoded frames with the worker via SharedArrayBuffer,
  // which is only exposed in a cross-origin-isolated context (COOP+COEP). When
  // the server forgets those headers, SAB is silently absent and the worker
  // dies obscurely mid-warmup. Fail here with an actionable message instead.
  if (typeof SharedArrayBuffer === 'undefined' || !self.crossOriginIsolated) {
    throw new Error(
      'Cross-origin isolation required (SharedArrayBuffer unavailable) — ' +
        'server must send COOP (Cross-Origin-Opener-Policy: same-origin) + ' +
        'COEP (Cross-Origin-Embedder-Policy: require-corp) headers.',
    );
  }
}

/**
 * Wraps a TS-ZapPlayer + the existing <video> stage so the legacy controls
 * still work. Returns a `detach()` function.
 *
 * @param {HTMLVideoElement} video       Existing native <video>.
 * @param {HTMLElement}      stage       Container holding video + chrome.
 * @param {string}           manifestUrl m3u8 URL from POST /play/start.
 * @param {object}           opts        { contentId, onError, onReady }
 */
export async function attachZapdEngine(video, stage, manifestUrl, opts = {}) {
  // Hard-fail with friendly message on browsers that can't run the engine.
  // No fallback path since hls.js retirement.
  try {
    assertEngineSupported();
  } catch (err) {
    const friendly = explainAttachError(err);
    showAttachErrorOverlay(stage, friendly);
    opts.onError?.(new Error(friendly));
    throw err;
  }

  // Build canvas overlay on top of the (now hidden) video.
  const canvas = document.createElement('canvas');
  canvas.style.position = 'absolute';
  canvas.style.inset = '0';
  canvas.style.width = '100%';
  canvas.style.height = '100%';
  canvas.style.background = '#000';
  canvas.style.objectFit = 'contain';
  // Place behind the controls layer (controls expect z-index ≥ 2 in our CSS).
  canvas.style.zIndex = '0';
  canvas.dataset.zapdEngineCanvas = '1';

  // Hide the original video but keep it in DOM so scrubber/buffered logic
  // (which reads video.currentTime + duration) continues to work.
  const prevDisplay = video.style.display;
  video.style.display = 'none';
  // We DO want video.duration/currentTime to be settable by us — but
  // setting currentTime on a video without src is allowed and a no-op.
  video.removeAttribute('src');
  video.load(); // detach old MSE source-buffer if any

  // Save native play/pause for restoration on detach. Patches are
  // applied AFTER engine instantiation below.
  const nativePlay = video.play.bind(video);
  const nativePause = video.pause.bind(video);

  // Attach canvas next to the video.
  if (!stage.style.position) stage.style.position = 'relative';
  stage.insertBefore(canvas, video);

  // Auto-detect WebGPU-adapter availability. We must try BOTH preferences
  // because:
  //   - 'high-performance' returns null on integrated-only systems (most
  //     intel-MacBooks, some laptops) even when WebGPU is fully available.
  //   - default (no preference) is what `WebGpuRenderer.create()` actually
  //     uses — that's the real "is renderer usable?" probe.
  // Only skip the renderer when BOTH options fail, i.e. genuinely no GPU
  // (headless server-chromes with software fallback fail this way).
  let skipRenderer = opts.skipRenderer ?? false;
  if (!skipRenderer) {
    if (!navigator.gpu) {
      skipRenderer = true;
    } else {
      let adapter = null;
      try {
        adapter = await navigator.gpu.requestAdapter();
      } catch {
        /* fall through */
      }
      if (!adapter) {
        try {
          adapter = await navigator.gpu.requestAdapter({ powerPreference: 'low-power' });
        } catch {
          /* fall through */
        }
      }
      if (!adapter) skipRenderer = true;
    }
  }

  // Spawn worker-engine (= MainThreadBridge transfers OffscreenCanvas,
  // runs decode + render off-main thread). Single-thread legacy-pfad
  // entfernt 2026-05-10 Phase 10e (= war für A/B-regression-testing,
  // worker-pfad seit 2026-05-08 stable).
  const engine = new MainThreadBridge({
    manifestUrl: manifestUrl || undefined,
    contentId: opts.contentId,
    skipRenderer,
    // Mid-playback session recovery: zapd may OOM-restart or reap the
    // session. Caller passes refreshSession so the bridge can issue a
    // fresh /play/start without losing playback position.
    refreshSession: opts.refreshSession,
    // A-P1.1 Browser-DirectPlay: mint a fresh /stream sid + DirectIo when a
    // byte-range read 404s (session reaped). Only set on the direct path.
    onDirectRecover: opts.onDirectRecover,
  });

  // Expose for debugging — read-only from devtools.
  window.__zapEngine = engine;
  window.__zapCanvas = canvas;

  // Probe-data snapshot from /api/probe. Plumbed through bridge so the
  // debug-overlay can read it without re-fetching. zap-player.js calls
  // bridge.setMediaInfo(info) once the probe Promise resolves.
  let mediaInfoSnapshot = null;

  // Wire engine-events to the surrounding player-shell.
  const offReady = engine.on('ready', () => {
    // Object.defineProperty alone does NOT auto-dispatch any media-event,
    // so the surrounding scrubber/time-display never repaints. We must
    // override the getter AND fire the events the UI actually listens
    // for: durationchange, loadedmetadata, canplay.
    try {
      Object.defineProperty(video, 'duration', {
        configurable: true,
        get: () => engineDurationSec(engine),
      });
    } catch {
      /* noop on browsers that disallow override */
    }
    // Override `seekable` to a single TimeRanges-like span [0, duration]
    // so scrubber-grab works.
    try {
      Object.defineProperty(video, 'seekable', {
        configurable: true,
        get: () => makeTimeRanges(0, engineDurationSec(engine)),
      });
    } catch {
      /* noop */
    }

    // Tell the legacy player-shell that metadata is ready. These three
    // are the events that hls.js fires on its own MediaSource attach.
    try {
      video.dispatchEvent(new Event('loadedmetadata'));
    } catch {
      /* noop */
    }
    try {
      video.dispatchEvent(new Event('durationchange'));
    } catch {
      /* noop */
    }
    try {
      video.dispatchEvent(new Event('loadeddata'));
    } catch {
      /* noop */
    }
    try {
      video.dispatchEvent(new Event('canplay'));
    } catch {
      /* noop */
    }
    try {
      video.dispatchEvent(new Event('canplaythrough'));
    } catch {
      /* noop */
    }

    // Codec-path-badge: shows in the corner whether decode is HW or SW.
    try {
      mountCodecBadge(stage, engine);
    } catch {
      /* noop */
    }
    // Live debug-overlay. Default visible (per current dev-state); user
    // toggles with `D` (calls window.toggleZapdDebug). Initial hidden if
    // localStorage.zapdDebug === '0' or `?debug=0`. The overlay always
    // mounts so the toggle target exists from frame 0.
    try {
      mountDebugOverlay(stage, engine, () => mediaInfoSnapshot);
    } catch {
      /* noop */
    }

    opts.onReady?.();
  });
  const offFirst = engine.on('firstFrame', () => {
    try {
      video.dispatchEvent(new Event('playing'));
    } catch {
      /* noop */
    }
    opts.onFirstFrame?.();
  });
  const offError = engine.on('error', (e) => opts.onError?.(e));
  const offAudioFail = engine.on('audioSwitchFailed', (reason) =>
    opts.onAudioSwitchFailed?.(reason),
  );

  // Start the engine — this fetches manifest + spawns decoders.
  // attach() throws on manifest-fetch errors (404/502), unsupported
  // codec (HEVC on Firefox), or WASM-init failure. We MUST surface that
  // to the user via opts.onError + a visible error overlay; otherwise
  // the modal sits there with a black canvas and no duration.
  try {
    // MainThreadBridge takes the canvas and calls
    // canvas.transferControlToOffscreen() internally. A-P1.1: when opts.direct
    // is present, drive the Browser-DirectPlay path instead of HLS.
    if (opts.direct) {
      await engine.attachDirect(canvas, opts.direct);
    } else {
      await engine.attach(canvas);
    }
  } catch (err) {
    // Tear down what little we set up so re-attach starts clean.
    const teardown = () => {
      try {
        canvas.remove();
      } catch {
        /* noop */
      }
      try {
        video.style.display = prevDisplay;
      } catch {
        /* noop */
      }
      try {
        video.play = nativePlay;
      } catch {
        /* noop */
      }
      try {
        video.pause = nativePause;
      } catch {
        /* noop */
      }
      try {
        offReady();
        offFirst();
        offError();
        offAudioFail();
      } catch {
        /* noop */
      }
    };
    // Direct path: EVERY attach failure is an expected fallback (unsupported
    // codec / HDR / deployed wasm without zapd_open_stream / wedged open). NO
    // error overlay, NO onError — the caller silently retries via HLS. Detach
    // the engine (disposes the DirectIo + fetch-worker) and rethrow so the
    // caller can route to HLS.
    if (opts.direct) {
      teardown();
      try {
        engine.detach();
      } catch {
        /* noop */
      }
      throw err;
    }
    const friendly = explainAttachError(err);
    opts.onError?.(new Error(friendly));
    showAttachErrorOverlay(stage, friendly);
    teardown();
    throw err;
  }

  // Now that the engine exists, patch video.play/pause to drive it.
  video.play = async function patchedPlay() {
    try {
      video.dispatchEvent(new Event('playing'));
    } catch {
      /* noop */
    }
    return engine.play();
  };
  video.pause = function patchedPause() {
    try {
      video.dispatchEvent(new Event('pause'));
    } catch {
      /* noop */
    }
    return engine.pause();
  };

  // Mirror media-clock to video.currentTime so the legacy scrubber updates.
  // 250ms cadence + an explicit `timeupdate` dispatch (HTMLMediaElement
  // normally fires this every 250ms by spec — we replicate that since
  // the engine drives the canvas, not the <video>'s native MSE).
  let lastMirroredCt = 0;
  let lastMirroredDuration = 0;
  // Override video.currentTime so both reads (UI display) and writes
  // (scrubber-drag) flow through the engine.
  try {
    Object.defineProperty(video, 'currentTime', {
      configurable: true,
      get: () => engine.currentTimeSec ?? 0,
      set: (t) => {
        const target = Math.max(0, Number(t) || 0);
        engine.seek(target);
        try {
          video.dispatchEvent(new Event('seeking'));
        } catch {
          /* noop */
        }
      },
    });
  } catch {
    /* noop */
  }

  // Override video.duration so the controls-bar's updateTime() can read
  // the engine-known duration even though the <video> element never gets
  // a real media-source. Without this, dur=0 → time-label stays "0:00/—:—"
  // forever for zapd-engine sessions.
  try {
    Object.defineProperty(video, 'duration', {
      configurable: true,
      get: () => engineDurationSec(engine) || NaN,
    });
  } catch {
    /* noop */
  }

  // Override video.paused with the engine's real play/pause state. The
  // hidden <video> never gets a MediaSource (the engine drives a canvas),
  // so its native `paused` stays permanently true — which made
  // zap-player.togglePlayPause() always take the play-branch and never
  // pause. Reading engine.paused fixes the controls-bar + spacebar toggle.
  try {
    Object.defineProperty(video, 'paused', {
      configurable: true,
      get: () => engine.paused,
    });
  } catch {
    /* noop */
  }

  const mirrorTimer = setInterval(() => {
    try {
      const t = engine.currentTimeSec ?? 0;
      if (Math.abs(t - lastMirroredCt) > 0.05) {
        lastMirroredCt = t;
        try {
          video.dispatchEvent(new Event('timeupdate'));
        } catch {
          /* noop */
        }
      }
      // Phase E: bei duration-known fire durationchange — controls-bar
      // wechselt dann von "0:00/—:—"-pulse zu "0:00/X:XX:XX"-final.
      const d = engineDurationSec(engine);
      if (d > 0 && d !== lastMirroredDuration) {
        lastMirroredDuration = d;
        try {
          video.dispatchEvent(new Event('durationchange'));
        } catch {
          /* noop */
        }
      }
    } catch {
      /* noop */
    }
  }, 250);

  // currentTime-setter (above) already routes through engine.seek; we
  // just need to fire a `seeked` once the engine reports completion so
  // the scrubber-drag can release.
  const offSeekDone = engine.on('seekComplete', (t) => {
    lastMirroredCt = t;
    try {
      video.dispatchEvent(new Event('seeked'));
    } catch {
      /* noop */
    }
    try {
      video.dispatchEvent(new Event('timeupdate'));
    } catch {
      /* noop */
    }
  });

  return {
    /** Public start: caller invokes once user-gesture happened. */
    play: () => engine.play(),
    pause: () => engine.pause(),
    seek: (t) => engine.seek(t),

    /** Switch audio-rendition on-the-fly. URL = the rendition's .m3u8.
     *  resumeSec (optional) = position the audio-orchestrator should resume
     *  at, MUST match the position passed to server's /switch — otherwise
     *  client requests segs before encoder's start_seg → 503-loop. */
    setAudioRendition: (url, resumeSec) => engine.setAudioRendition?.(url, resumeSec),

    /** A-P1.1 direct-mode audio-track switch. streamIndex = GLOBAL libav
     *  source-stream index (the SPA resolves the ordinal → stream first).
     *  Video keeps running; only the worker's audio decoder repoints. */
    setDirectAudioTrack: (streamIndex) => engine.setDirectAudioTrack?.(streamIndex),

    /** Active pipeline — 'direct' (A-P1.1) or 'hls'. Debug-overlay signal. */
    getMode: () => engine.getMode?.() ?? 'hls',

    /** Frame-by-frame step during pause. +N forward, -N backward. */
    frameStep: (frames) => engine.frameStep?.(frames),

    /** Diagnostik-Passthrough: praesentierte Frames (Engine-Zaehler). Wird
     *  vom Debug-Overlay genutzt; auch fuer E2E/Scrub-Verifikation lesbar. */
    getPresentedFrameCount: () => engine.getPresentedFrameCount?.() ?? 0,

    /** Plumb /api/probe result into the debug-overlay. Source-codec /
     *  container / size / duration come from this — updated once when the
     *  probe Promise resolves. Safe to call multiple times. */
    setMediaInfo: (info) => {
      mediaInfoSnapshot = info || null;
    },

    /** Phase E1.1: pin the engine's zapd session-id so the debug-overlay
     *  can match its session_hash against /api/system/stats and
     *  /api/hls/events filter messages for THIS session vs others. */
    setSessionId: (sid) => {
      engine.sessionId = sid || null;
    },

    /** Tear down. */
    detach: () => {
      try {
        offReady();
      } catch {
        /* noop */
      }
      try {
        offFirst();
      } catch {
        /* noop */
      }
      try {
        offError();
      } catch {
        /* noop */
      }
      try {
        offAudioFail();
      } catch {
        /* noop */
      }
      try {
        offSeekDone();
      } catch {
        /* noop */
      }
      try {
        engine.detach();
      } catch {
        /* noop */
      }
      clearInterval(mirrorTimer);
      try {
        stage.querySelector('[data-zap-codec-badge]')?.remove();
      } catch {
        /* noop */
      }
      try {
        stage.querySelector('[data-zap-attach-error]')?.remove();
      } catch {
        /* noop */
      }
      try {
        stage.querySelector('[data-zap-debug]')?.remove();
      } catch {
        /* noop */
      }
      try {
        canvas.remove();
      } catch {
        /* noop */
      }
      try {
        video.style.display = prevDisplay;
      } catch {
        /* noop */
      }
      // Restore native play/pause.
      try {
        video.play = nativePlay;
      } catch {
        /* noop */
      }
      try {
        video.pause = nativePause;
      } catch {
        /* noop */
      }
      // Drop the paused-getter override so a reused <video> reflects its
      // own native state again (the duration/currentTime overrides are
      // left as-is, matching pre-existing detach behaviour).
      try {
        delete video.paused;
      } catch {
        /* noop */
      }
    },
  };
}

/**
 * Build a TimeRanges-shaped object covering [start, end]. Native
 * TimeRanges is not constructable, but the surrounding scrubber only
 * reads `length`, `start(0)`, and `end(0)`.
 */
function makeTimeRanges(start, end) {
  const safe = (n) => (Number.isFinite(n) ? n : 0);
  return {
    length: 1,
    start: () => safe(start),
    end: () => safe(end),
  };
}

/**
 * Mount a small "HW HEVC" / "SW H.264" badge in the stage so the user
 * can see at-a-glance whether the current playback is hardware-decoded
 * (battery-friendly) or software-decoded (hot CPU).
 *
 * The detection is best-effort — WebCodecs has no W3C API to introspect
 * after configure() whether HW was actually engaged. We rely on
 * isConfigSupported(prefer-hardware) which is trustworthy on chrome/safari
 * (false negatives possible on niche hardware, false positives extremely rare).
 */
function mountCodecBadge(stage, engine) {
  const path = engine.getDecodePath?.();
  if (!path) return;
  // Avoid stacking badges across re-attach.
  stage.querySelector('[data-zap-codec-badge]')?.remove();

  const el = document.createElement('div');
  el.dataset.zapCodecBadge = '1';
  el.style.position = 'absolute';
  el.style.top = '8px';
  el.style.left = '8px';
  el.style.zIndex = '10';
  el.style.padding = '3px 8px';
  el.style.borderRadius = '4px';
  el.style.font = '11px/1.4 -apple-system, BlinkMacSystemFont, sans-serif';
  el.style.fontWeight = '600';
  el.style.letterSpacing = '0.4px';
  el.style.background = path.videoHw ? 'rgba(20, 140, 70, 0.85)' : 'rgba(190, 100, 40, 0.85)';
  el.style.color = '#fff';
  el.style.pointerEvents = 'none';
  el.style.userSelect = 'none';
  el.style.boxShadow = '0 1px 4px rgba(0,0,0,0.4)';
  el.title = path.videoHw
    ? 'Hardware-decoded — battery-friendly path (GPU video-decoder).'
    : 'Software-decoded — CPU running the codec. Higher battery + heat.';
  // Codec string from WebCodecs (e.g. "avc1.640029" → "H.264 High@4.1").
  const friendly = friendlyCodecName(path.videoCodec);
  el.textContent =
    `${path.videoHw ? '🟢 HW' : '🟠 SW'} · ${friendly}` +
    (path.audioCodec ? ` · ${friendlyCodecName(path.audioCodec)}` : '');
  stage.appendChild(el);
}

/**
 * Translate raw attach() errors into user-facing messages. The most
 * common reason is the zapd transcoder failing on exotic source codecs
 * (VC-1, MS-MPEG4, RealVideo, ...) — for those the manifest endpoint
 * returns 502 Bad Gateway. We show the user the cause they can act on.
 */
function explainAttachError(err) {
  const msg = (err && (err.message || String(err))) || '';
  if (/\bHTTP 502\b/i.test(msg))
    return 'Server konnte den Stream nicht erzeugen (502). Quell-codec ist möglicherweise nicht transkodierbar (z.B. VC-1) oder der Encoder ist gerade abgestürzt. Versuche es in 30 Sekunden noch einmal.';
  if (/\bHTTP 401\b/i.test(msg))
    return 'Session abgelaufen — bitte Seite neu laden und erneut anmelden.';
  if (/\bHTTP 404\b/i.test(msg))
    return 'Datei nicht gefunden oder Wiedergabe-Session vom Server entfernt. Seite neu laden.';
  if (/Unsupported video codec/i.test(msg))
    return 'Video-Codec wird vom Browser nicht unterstützt. Bei HEVC/AV1: chrome://flags/#enable-experimental-web-platform-features prüfen.';
  if (/WebGPU not supported/i.test(msg))
    return 'WebGPU nicht verfügbar — Browser zu alt oder Hardware-Beschleunigung deaktiviert.';
  if (/No GPU adapter/i.test(msg))
    return 'Keine GPU verfügbar (headless oder GPU-deaktiviert). Browser braucht hardware-rendering für die Wiedergabe.';
  return `Wiedergabe-Engine konnte nicht starten: ${msg.slice(0, 200)}`;
}

function showAttachErrorOverlay(stage, msg) {
  stage.querySelector('[data-zap-attach-error]')?.remove();
  const el = document.createElement('div');
  el.dataset.zapAttachError = '1';
  el.style.position = 'absolute';
  el.style.inset = '0';
  el.style.display = 'flex';
  el.style.alignItems = 'center';
  el.style.justifyContent = 'center';
  el.style.background = 'rgba(20, 22, 28, 0.92)';
  el.style.color = '#e8eaef';
  el.style.font = '14px/1.5 -apple-system, BlinkMacSystemFont, sans-serif';
  el.style.padding = '24px';
  el.style.zIndex = '20';
  el.style.textAlign = 'center';
  el.innerHTML = `<div style="max-width:520px"><div style="font-size:32px;margin-bottom:8px">⚠</div><div style="font-weight:600;margin-bottom:6px">Wiedergabe nicht möglich</div><div style="opacity:0.85">${escapeHtml(msg)}</div></div>`;
  if (!stage.style.position) stage.style.position = 'relative';
  stage.appendChild(el);
}

function escapeHtml(s) {
  return String(s).replace(
    /[&<>"']/g,
    (ch) =>
      ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      })[ch] || ch,
  );
}

/**
 * Initial visibility of the debug-overlay.
 *
 * Resolution order:
 *   1. URL `?debug=1` / `?debug=0` — explicit, wins everything.
 *   2. localStorage `zapdDebug` — '1' or '0', set by the toggle (stats button / D-key).
 *   3. Default OFF — the overlay is opt-in; toggle it on via the stats button
 *      in the control bar or the `D` key.
 */
function isDebugOn() {
  try {
    const v = new URL(location.href).searchParams.get('debug');
    if (v === '1') return true;
    if (v === '0') return false;
    const ls = localStorage.getItem('zapdDebug');
    if (ls === '1') return true;
    if (ls === '0') return false;
  } catch {
    /* noop */
  }
  return false; // ship-default OFF (opt-in)
}

const fmtBytes = (n) => {
  if (!n || n < 0) return '—';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let x = +n;
  while (x >= 1024 && i < u.length - 1) {
    x /= 1024;
    i++;
  }
  return x.toFixed(x >= 100 ? 0 : 1) + ' ' + u[i];
};
const fmtDur = (s) => {
  if (!s || s < 1) return '—';
  const h = Math.floor(s / 3600),
    m = Math.floor((s % 3600) / 60),
    sec = Math.floor(s % 60);
  return h
    ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
    : `${m}:${String(sec).padStart(2, '0')}`;
};
const fmtMbps = (bps) => (bps && bps > 0 ? (bps / 1_000_000).toFixed(2) + ' Mb/s' : '—');

/**
 * On-screen live-stats overlay. Top-right of the stage, monospace.
 * Updates every 200 ms (queue/clock) with FPS averaged over the last 5
 * ticks (= 1 s) for stable read-outs.
 *
 * Sections (top→bottom):
 *   - Source       — from /api/probe (codec, container, size, duration)
 *   - Live Decode  — decoder-config (HW/SW + actual coded dims)
 *   - Stream       — variant-bitrate + observed bandwidth + buffer-ahead
 *   - Performance  — playback-FPS + decoded-FPS + drops + queue-depth
 *   - Clock        — video clock + audio clock + skew
 *
 * Toggle: `D` key (via window.toggleZapdDebug) — persisted in
 * localStorage `zapdDebug`. The element stays mounted while hidden so
 * the FPS-Δ counters don't reset on toggle.
 */
function mountDebugOverlay(stage, engine, getMediaInfo) {
  stage.querySelector('[data-zap-debug]')?.remove();
  const el = document.createElement('div');
  el.dataset.zapDebug = '1';
  Object.assign(el.style, {
    position: 'absolute',
    top: '8px',
    right: '8px',
    zIndex: '11',
    padding: '8px 12px',
    borderRadius: '4px',
    background: 'rgba(0,0,0,0.78)',
    color: '#9efb9e',
    font: '11px/1.5 ui-monospace, Menlo, Consolas, monospace',
    pointerEvents: 'none',
    userSelect: 'none',
    whiteSpace: 'pre',
    minWidth: '290px',
    maxWidth: '420px',
    boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
  });
  el.hidden = !isDebugOn();
  stage.appendChild(el);
  // Worker stats cadence follows overlay visibility (200ms vs 2s).
  engine.setStatsEnabled?.(!el.hidden);

  // Phase E1.1: subscribe to backend-event buses for live pipeline +
  // resource info. The buses are singletons (start() is idempotent),
  // so calling start() here is safe even if other views also use them.
  let systemSnapshot = null; // most recent /api/system/stats tick
  let hlsState = {
    // accumulated from /api/hls/events
    phase: null,
    phase_progress: null,
    phase_eta_ms: null,
    phase_state: null,
    format_decision: null,
  };
  let unsubSystem = () => {};
  let unsubHls = () => {};
  (async () => {
    try {
      systemBus.start();
      hlsBus.start();
      systemSnapshot = systemBus.getLatest();
      unsubSystem = systemBus.subscribe((evt) => {
        systemSnapshot = evt;
      });
      unsubHls = hlsBus.subscribe(
        () => true,
        (evt) => {
          if (!evt || evt.source !== 'hls') return;
          const d = evt.data || {};
          // Match-or-accept-first-seen: engine.sessionId is set by
          // lifecycle.js after backendStart resolves; if not set yet,
          // accept any hls event so the overlay isn't empty during
          // probe→encode_warmup window.
          const mySid = engine.sessionId;
          if (mySid && d.session_hash && d.session_hash !== mySid) return;
          if (evt.event === 'session.phase') {
            hlsState.phase = d.phase ?? hlsState.phase;
            hlsState.phase_progress = d.phase_progress_pct ?? null;
            hlsState.phase_eta_ms = d.phase_eta_ms ?? null;
            hlsState.phase_state = d.state || hlsState.phase_state;
          } else if (evt.event === 'session.start') {
            hlsState.phase_state = d.state || 'probing';
            hlsState.phase = null;
          } else if (evt.event === 'session.format-decision') {
            hlsState.format_decision = d.decisions || d;
          } else if (evt.event === 'session.stop') {
            hlsState.phase_state = 'stopped';
          }
        },
      );
    } catch (e) {
      console.warn('debug-overlay: failed to load buses', e);
    }
  })();

  // Ring-buffer of (presented, decoded, timestamp) over the last 5 ticks
  // (= 1 s @ 200 ms interval). FPS = (newest - oldest) / dt — much steadier
  // than per-tick Δ which jitters by ±5 fps on sub-second decode bursts.
  const ringSize = 5;
  const ring = []; // each entry: { ts, presented, decoded }

  const tick = () => {
    if (!stage.contains(el)) return; // detached
    // Hidden → skip sampling entirely (the fps ring refills within ~1s of
    // opening; no reason to poll engine getters 5×/s for an invisible box).
    if (el.hidden) return;

    const now = performance.now();
    const presNow = engine.getPresentedFrameCount?.() ?? 0;
    const decNow = engine.getDecodedFrameCount?.() ?? 0;
    ring.push({ ts: now, presented: presNow, decoded: decNow });
    while (ring.length > ringSize) ring.shift();

    let playbackFps = 0;
    let decodeFps = 0;
    if (ring.length >= 2) {
      const oldest = ring[0];
      const dt = (now - oldest.ts) / 1000;
      if (dt > 0) {
        playbackFps = Math.round((presNow - oldest.presented) / dt);
        decodeFps = Math.round((decNow - oldest.decoded) / dt);
      }
    }

    const q = engine.frameQueue || [];
    const qH = q[0]?.timestamp;
    const qT = q[q.length - 1]?.timestamp;
    const ct = engine.currentTimeSec ?? 0;
    const ac = engine.audioRenderer?.currentTimeSec ?? 0;
    const wQ = engine.videoPool?.warmQueueSize ?? 0;
    const path = engine.getDecodePath?.() || {};
    const drops = engine.getDroppedCounts?.() || { past: 0, overflow: 0, future: 0 };
    const bufEnd = engine.getBufferEndSec?.() ?? 0;
    const bwBps = engine.getBandwidthBps?.() ?? 0;
    const variantBps = engine.getCurrentBitrateBps?.() ?? 0;
    const liveSize = engine.getVideoSize?.() || { width: null, height: null };
    const skewMs = Math.round((ct - ac) * 1000);
    const bufAhead = Math.max(0, bufEnd - ct);

    const m = getMediaInfo?.() || null;
    const liveMode = engine.getMode?.() ?? 'hls';
    const sourceLines = m
      ? [
          `  Quelle:  ${
            liveMode === 'direct'
              ? 'Original-Container (byte-range)'
              : m.canDirectPlay
                ? 'Direct-Play (kein Transcode)'
                : 'HLS-Transcode'
          }`,
          `  Video:   ${m.videoCodec || '?'} ${m.srcWidth || '?'}×${m.srcHeight || '?'}`,
          `  Audio:   ${m.audioCodec || '?'}${m.audioChannels ? ' ' + m.audioChannels + '-Kanal' : ''}`,
          `  Datei:   ${m.container || '?'} · ${fmtBytes(m.sizeBytes)} · ${fmtDur(m.durationSec)}`,
        ]
      : [`  (warte auf /api/probe…)`];

    const liveDimsStr =
      liveSize.width && liveSize.height ? ` ${liveSize.width}×${liveSize.height}` : '';
    const friendlyV = friendlyCodecName(path.videoCodec);
    // A-P1.1: which pipeline is live (Direct byte-range vs HLS transcode).
    const modeStr = liveMode === 'direct' ? 'DirectPlay (Original)' : 'HLS';

    el.textContent = [
      `Source`,
      ...sourceLines,
      ``,
      `Live Decode`,
      `  ${path.videoHw ? '🟢 HW' : '🟠 SW'} ${friendlyV}${liveDimsStr}`,
      `  Mode:    ${modeStr}`,
      ``,
      `Stream`,
      `  Bitrate: ${fmtMbps(variantBps)} (variant)`,
      `  Bandbr:  ${fmtMbps(bwBps * 8)} (estimate)`,
      `  Buffer:  ${bufAhead.toFixed(1)}s ahead`,
      ``,
      `Performance`,
      `  FPS:     ${playbackFps} playback · ${decodeFps} decoded`,
      `  Drops:   ${drops.past}p · ${drops.overflow}o · ${drops.future}f`,
      `  Queue:   ${q.length} / ${engine.maxFrameQueueLen ?? '?'} · warmQ: ${wQ}`,
      `  Head→T:  ${qH != null ? (qH / 1e6).toFixed(2) : '—'}s → ${qT != null ? (qT / 1e6).toFixed(2) : '—'}s`,
      ``,
      `Clock`,
      `  Video:   ${ct.toFixed(2)}s · Audio: ${ac.toFixed(2)}s`,
      `  Skew:    ${skewMs >= 0 ? '+' : ''}${skewMs}ms`,
      ``,
      ...renderBackendSections(systemSnapshot, hlsState, engine.sessionId),
    ].join('\n');
  };

  const handle = setInterval(tick, 200);
  tick();

  // Expose toggle for D-key + devtools. Persists state in localStorage so
  // reload preserves user's choice.
  window.toggleZapdDebug = () => {
    el.hidden = !el.hidden;
    try {
      localStorage.setItem('zapdDebug', el.hidden ? '0' : '1');
    } catch {
      /* noop */
    }
    engine.setStatsEnabled?.(!el.hidden);
    if (!el.hidden) tick(); // immediate refresh on show
    return !el.hidden;
  };

  // Auto-cleanup if engine detaches
  const obs = new MutationObserver(() => {
    if (!stage.contains(el)) {
      clearInterval(handle);
      obs.disconnect();
      try {
        unsubSystem();
      } catch {}
      try {
        unsubHls();
      } catch {}
      try {
        delete window.toggleZapdDebug;
      } catch {
        /* noop */
      }
    }
  });
  obs.observe(stage, { childList: true });
}

/**
 * Render the three backend-trace sections (Pipeline / Backend Live /
 * Backend Resources) for the debug-overlay. Phase E1.1.
 *
 * Inputs:
 *   sysSnap    — most recent /api/system/stats tick (or null while
 *                connecting)
 *   hlsState   — accumulated state from /api/hls/events (phase,
 *                format-decision, last seen state)
 *   mySid      — engine.sessionId (matches systemSnapshot.sessions
 *                entry); falls back to first session if not set yet
 */
function renderBackendSections(sysSnap, hlsState, mySid) {
  const lines = [];

  // ── Pipeline ─────────────────────────────────────────────────────────
  lines.push('Pipeline');
  if (hlsState.phase || hlsState.phase_state) {
    const phase = hlsState.phase || hlsState.phase_state || '—';
    let pl = `  Phase:   ${phase}`;
    if (hlsState.phase_progress != null) {
      const filled = Math.max(0, Math.min(8, Math.round(hlsState.phase_progress / 12.5)));
      pl += `  ${'█'.repeat(filled)}${'░'.repeat(8 - filled)} ${hlsState.phase_progress}%`;
    }
    if (hlsState.phase_eta_ms != null && hlsState.phase_eta_ms > 0) {
      pl += `  (eta ${(hlsState.phase_eta_ms / 1000).toFixed(1)}s)`;
    }
    lines.push(pl);
  } else {
    lines.push(`  Phase:   (warte auf zapd…)`);
  }
  const fd = hlsState.format_decision;
  if (fd && fd.video) {
    const v = fd.video;
    let vline = `  Video:   `;
    if (v.mode === 'passthrough' || v.mode === 'copy') {
      vline += `${(v.input_codec || '?').toUpperCase()} → passthrough`;
    } else {
      vline += `${(v.input_codec || '?').toUpperCase()} → ${(v.output_codec || 'h264').toUpperCase()}`;
      if (v.scale && v.scale.from !== v.scale.to) {
        vline += ` ${v.scale.from}→${v.scale.to}`;
      }
    }
    lines.push(vline);
    if (v.color && (v.color.from || v.color.to)) {
      lines.push(
        `           ${v.color.from || '?'} → ${v.color.to || '?'}` +
          (v.color.tonemap ? `  tonemap: ${v.color.tonemap}` : ''),
      );
    }
    if (v.reason) lines.push(`           reason: ${v.reason}`);
  }
  if (fd && Array.isArray(fd.audio) && fd.audio.length) {
    for (const a of fd.audio) {
      const tag = a.mode === 'passthrough' || a.mode === 'copy' ? 'pass' : 'transcode';
      const codecStr =
        (a.input_codec || '?').toUpperCase() +
        (tag === 'pass' ? '' : ' → ' + (a.output_codec || 'aac').toUpperCase());
      const lang = a.language ? ` [${a.language}]` : '';
      const ch = a.channels ? ` ${a.channels}ch` : '';
      lines.push(`  Audio:   #${a.track ?? '?'} ${codecStr}${ch}${lang}`);
      if (a.reason && tag !== 'pass') {
        lines.push(`           reason: ${a.reason}`);
      }
    }
  }
  if (fd && Array.isArray(fd.subtitles) && fd.subtitles.length) {
    for (const s of fd.subtitles) {
      const lang = s.language ? ` [${s.language}]` : '';
      const tag = s.mode === 'webvtt_burnin_ocr' ? 'OCR→WebVTT' : 'WebVTT';
      lines.push(
        `  Subs:    #${s.track ?? '?'} ${(s.codec || '?').toUpperCase()}${lang}  → ${tag}`,
      );
    }
  }
  if (fd && fd.dv_el_skipped) {
    lines.push(`  DV:      el_skipped` + (fd.dv_skip_reason ? ` (${fd.dv_skip_reason})` : ''));
  }
  lines.push('');

  // ── Backend Live (per-session) ───────────────────────────────────────
  lines.push('Backend Live');
  const sessions = sysSnap?.data?.sessions || [];
  let me = mySid ? sessions.find((s) => s.sid === mySid) : null;
  if (!me && sessions.length === 1) me = sessions[0];
  if (me) {
    const ageStr =
      me.last_fetched_age_ms >= 0 ? `${(me.last_fetched_age_ms / 1000).toFixed(1)}s ago` : '—';
    lines.push(`  sid:     ${me.sid.slice(0, 12)} · gen ${me.generation}`);
    lines.push(
      `  Encode:  target seg=${me.current_encode_target ?? '?'} · max cached=${me.max_seen_cached ?? '?'}`,
    );
    lines.push(`  Fetch:   last seg=${me.last_fetched_seg ?? '?'} (${ageStr})`);
    if (me.discontinuities) {
      lines.push(`  Disc:    ${me.discontinuities}`);
    }
  } else {
    lines.push(`  (keine Session)`);
  }
  lines.push('');

  // ── Backend Resources (zapd aggregate) ───────────────────────────────
  lines.push('Backend Resources');
  if (sysSnap?.data) {
    const d = sysSnap.data;
    lines.push(
      `  zapd:    CPU ${(d.cpu_pct ?? 0).toFixed(0)}%  RSS ${(d.rss_mib ?? 0).toFixed(0)} MiB`,
    );
    lines.push(
      `  System:  load1 ${(d.loadavg1 ?? 0).toFixed(2)} · ${d.n_sessions ?? 0} session${d.n_sessions === 1 ? '' : 's'}`,
    );
    if (d.gpu) lines.push(`  GPU:     ${d.gpu.summary || '?'}`);
  } else {
    lines.push(`  (warte auf /api/system/stats…)`);
  }

  return lines;
}

function friendlyCodecName(c) {
  if (!c) return '?';
  if (c.startsWith('avc1.') || c.startsWith('avc3.')) return 'H.264';
  if (c.startsWith('hev1.') || c.startsWith('hvc1.')) return 'HEVC';
  if (c.startsWith('av01.')) return 'AV1';
  if (c.startsWith('vp09.') || c === 'vp9') return 'VP9';
  if (c.startsWith('mp4a.40.')) return 'AAC';
  if (c === 'opus') return 'Opus';
  if (c === 'ac-3' || c === 'mp4a.a5') return 'AC-3';
  if (c === 'ec-3' || c === 'mp4a.a6') return 'E-AC-3';
  return c;
}

/**
 * Pull duration via internal getter — not strictly public on the TS-class
 * but stable enough for our use here. We keep the indirection so a typo
 * won't crash the legacy controls.
 */
function engineDurationSec(engine) {
  // Two engine implementations:
  //   - MainThreadBridge (worker path): exposes `durationSec` directly,
  //     populated from the worker's `ready` postMessage payload.
  //   - ZapPlayer (legacy single-thread): stores it on
  //     `media.totalDurationSec` from manifest-parser.
  try {
    if (typeof engine?.durationSec === 'number') return engine.durationSec;
    return Number(engine?.['media']?.['totalDurationSec']) || 0;
  } catch {
    return 0;
  }
}
